import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MessageSquare, Send, Inbox, Users, FileCheck, AlertTriangle, X, Mail } from "lucide-react";
import { motion } from "framer-motion";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

function EdenFailureBanner() {
  const [health, setHealth] = useState(null);
  const [dismissedAt, setDismissedAt] = useState(() => localStorage.getItem("edenBannerDismissedAt"));

  const load = () => api.get("/integrations/health").then((r) => setHealth(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  if (!health || health.failures_1h === 0) return null;
  if (dismissedAt && health.last_failure_at && dismissedAt >= health.last_failure_at) return null;

  const dismiss = () => {
    const ts = health.last_failure_at || new Date().toISOString();
    localStorage.setItem("edenBannerDismissedAt", ts);
    setDismissedAt(ts);
  };

  const when = health.last_failure_at ? new Date(health.last_failure_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

  return (
    <div
      className="mb-8 flex items-start justify-between gap-4 border-2 border-red-500 bg-red-50 px-5 py-4 dark:bg-red-950/40"
      data-testid="eden-failure-banner"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" />
        <div>
          <div className="font-display text-sm font-black uppercase tracking-[0.15em] text-red-700 dark:text-red-300">
            {health.failures_1h} Eden send{health.failures_1h === 1 ? "" : "s"} failed in the last hour
          </div>
          <div className="mt-1 text-xs text-red-900 dark:text-red-200">
            {health.last_failure_template && <span className="font-mono">{health.last_failure_template}</span>}
            {when && <span> · {when}</span>}
            {health.last_failure_message && <span> · {String(health.last_failure_message).slice(0, 120)}</span>}
          </div>
          <Link
            to="/keys"
            className="mt-2 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.18em] text-red-700 underline underline-offset-4 hover:text-red-900 dark:text-red-300 dark:hover:text-red-100"
            data-testid="eden-banner-open-activity"
          >
            Open Eden Activity <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
      <button
        onClick={dismiss}
        aria-label="Dismiss until next failure"
        data-testid="eden-banner-dismiss"
        className="text-red-500 transition-colors hover:text-red-800 dark:hover:text-red-100"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

function SendDigestButton() {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    try {
      const r = await api.post("/digest/send-now");
      const d = r.data || {};
      alert(
        `Digest sent to admin email.\n\n` +
        `Day: ${d.day_label}\n` +
        `Conversations: ${d.total_conversations}\n` +
        `Negative: ${d.counts?.negative ?? 0} · Unattended: ${d.counts?.unattended ?? 0} · OK: ${d.counts?.ok ?? 0}\n` +
        `PDF size: ${d.pdf_size ? Math.round(d.pdf_size/1024)+' KB' : '—'}`
      );
    } catch (err) {
      alert("Digest failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mb-8 flex items-center justify-between gap-4 border border-border bg-secondary/40 px-5 py-4">
      <div className="flex items-center gap-3">
        <Mail className="h-5 w-5 text-primary" />
        <div>
          <div className="font-display text-sm font-bold uppercase tracking-[0.15em]">Daily Digest</div>
          <div className="text-xs text-muted-foreground">Nightly at 9:00 PM IST · sends today&apos;s chat summary PDF to admin email. Sentiment + unattended flags via Claude.</div>
        </div>
      </div>
      <button
        onClick={onClick}
        disabled={busy}
        data-testid="send-digest-now-btn"
        className="border border-border bg-background px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] transition-colors hover:border-primary hover:text-primary disabled:opacity-50"
      >
        {busy ? "Sending…" : "Send digest now"}
      </button>
    </div>
  );
}

export default function DashboardHome() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    api
      .get("/stats")
      .then((r) => setStats(r.data))
      .finally(() => setLoading(false));
  }, []);

  const cards = [
    { label: "Total Conversations", value: stats?.total_conversations ?? 0, icon: MessageSquare, testid: "stat-total-conversations", big: true },
    { label: "Messages (24h)", value: stats?.messages_24h ?? 0, icon: Send, testid: "stat-messages-24h" },
    { label: "Inbound (24h)", value: stats?.inbound_24h ?? 0, icon: Inbox, testid: "stat-inbound-24h" },
    { label: "Outbound (24h)", value: stats?.outbound_24h ?? 0, icon: Send, testid: "stat-outbound-24h" },
    { label: "Active Staff", value: stats?.staff_count ?? 0, icon: Users, testid: "stat-staff" },
    { label: "Approved Templates", value: `${stats?.templates_approved ?? 0} / ${stats?.templates_total ?? 0}`, icon: FileCheck, testid: "stat-templates" },
  ];

  return (
    <div className="px-10 py-10">
      <header className="mb-10">
        <div className="text-xs font-bold uppercase tracking-[0.24em] text-muted-foreground">Overview</div>
        <h1 className="mt-2 font-display text-4xl font-black leading-none tracking-tight sm:text-5xl" data-testid="dashboard-title">
          Welcome back, {user?.name?.split(" ")[0] || "there"}.
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
          Here&apos;s the pulse of your customer conversations, staff activity and WhatsApp template pipeline.
        </p>
      </header>

      <section className="grid grid-cols-1 gap-0 border border-border md:grid-cols-3 lg:grid-cols-6">
        {cards.map((c, i) => (
          <motion.div
            key={c.label}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: i * 0.05 }}
            data-testid={c.testid}
            className={`relative border-border p-6 [&:not(:last-child)]:border-b md:[&:not(:last-child)]:border-b-0 md:[&:not(:last-child)]:border-r ${
              c.big ? "md:col-span-3 lg:col-span-2 md:row-span-1 bg-secondary" : ""
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-muted-foreground">{c.label}</div>
              <c.icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            </div>
            <div className={`mt-6 font-display font-black tracking-tight ${c.big ? "text-6xl" : "text-3xl"}`}>
              {loading ? "—" : c.value}
            </div>
          </motion.div>
        ))}
      </section>

      <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <QuickCard
          to="/conversations"
          title="Open Inbox"
          desc="Read incoming messages and see who is chatting with you."
          testid="quick-inbox"
        />
        <QuickCard
          to="/templates/new"
          title="Create Template"
          desc="Draft, add buttons, and submit a new template to Meta for approval."
          testid="quick-create-template"
        />
        <QuickCard
          to="/templates"
          title="Send Template"
          desc="Pick an approved template and message a customer instantly."
          testid="quick-send-template"
        />
      </section>
    </div>
  );
}

function QuickCard({ to, title, desc, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group flex flex-col justify-between border border-border p-6 transition-colors duration-200 hover:bg-secondary"
    >
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-muted-foreground">Shortcut</div>
        <h3 className="mt-3 font-display text-xl font-bold tracking-tight">{title}</h3>
        <p className="mt-2 text-sm text-muted-foreground">{desc}</p>
      </div>
      <div className="mt-8 flex items-center gap-1 text-xs font-bold uppercase tracking-[0.2em] text-primary">
        Open
        <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
      </div>
    </Link>
  );
}
